import { useCallback } from 'react'

import type { Map } from 'immutable'
import { useSelector } from 'react-redux'

import type { RecipeCoreId } from '@library/menu-service'

import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { getBasketRecipes, getBasketPostcode } from 'selectors/basket'

import { useRecipeLimitReached, sumQuantities } from '../limits'
import { useAddRecipe, useAddRecipeByUuid } from './useAddRecipe'
import { useRemoveRecipe, useRemoveRecipeByUuid } from './useRemoveRecipe'

/**
 * Can recipes be added to the basket at this point?
 *
 * Requires a postcode to be set
 *
 * @returns
 */
const useCanAddRecipes = () => {
  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const postcode = useSelector(getBasketPostcode)

  if (hasProspectNotSeenWizard) return true

  return Boolean(postcode)
}

/**
 * Get the recipes currently in the basket
 *
 * @returns a `Map<RecipeCoreId, number>` where the key is the recipe ID and the value is the number of that recipe in basket
 */
const useRecipeQuantities = () =>
  useSelector(getBasketRecipes) as unknown as Map<RecipeCoreId, number>

/**
 * Entrypoint for the `basket recipes` information
 *
 * @returns addRecipe - add a recipe to the basket
 * @returns removeRecipe - remove a recipe from the basket
 * @returns swapRecipe - replace a recipe in the basket
 *
 */
export const useBasketRecipes = () => {
  const recipeQuantities = useRecipeQuantities()

  const getQuantitiesForRecipeId = useCallback(
    (recipeId: RecipeCoreId) => recipeQuantities.get(recipeId, 0),
    [recipeQuantities],
  )

  const addRecipe = useAddRecipe()
  const addRecipeByUuid = useAddRecipeByUuid()
  const removeRecipe = useRemoveRecipe()
  const removeRecipeByUuid = useRemoveRecipeByUuid()
  const reachedLimit = useRecipeLimitReached(recipeQuantities)
  const canAddRecipes = useCanAddRecipes()

  const recipeCount = sumQuantities(recipeQuantities)

  return {
    addRecipe,
    addRecipeByUuid,
    removeRecipe,
    removeRecipeByUuid,
    canAddRecipes,
    reachedLimit,
    recipeCount,
    getQuantitiesForRecipeId,
  }
}
